<template>
  <div>
    <page-header>
      <template #title>
        <h1 class="page-title">Users</h1>
      </template>
      <template #actions>
        <page-header-action tag="form-button" @clicked="openModal">
          New User
        </page-header-action>
      </template>
    </page-header>
    <div class="row">
      <div class="col-lg-12">
        <div class="card mb-3">
          <div class="loading-overlay" v-if="loading"></div>

          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email Address</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody v-if="users.length">
                <tr v-bind:key="index" v-for="(user, index) in users">
                  <td>{{ user.first_name }} {{ user.last_name }}</td>
                  <td>{{ user.email }}</td>
                  <td>
                    <span class="badge badge-danger" v-if="!!user.locked_at">
                      Locked
                    </span>
                    <span class="badge badge-success" v-if="!user.locked_at">
                      Active
                    </span>
                  </td>
                  <td class="text-right">
                    <button
                      class="btn btn-info"
                      v-on:click.prevent="unlock(user)"
                      v-if="hasPermission('users.unlock') && !!user.locked_at"
                    >
                      Unlock
                    </button>

                    <button
                      class="btn btn-white"
                      v-on:click.prevent="lock(user)"
                      v-if="hasPermission('users.unlock') && !user.locked_at"
                    >
                      Lock
                    </button>

                    <button
                      class="btn btn-warning"
                      v-on:click.prevent="openEditModal(user)"
                    >
                      Edit
                    </button>

                    <button
                      class="btn btn-danger"
                      v-on:click.prevent="removeUser(user)"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              </tbody>
              <tbody v-if="!users.length">
                <tr>
                  <td colspan="6" class="text-center">
                    There are currently no users
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="card-footer text-right" v-show="showPagination">
            <pagination
              :data="paginator_data"
              v-on:updateResults="getData"
              :params="`&per_page=${perPage}`"
            ></pagination>
          </div>
        </div>
      </div>
    </div>

    <modal :show="modals.form" :large="true" v-on:close="closeModal">
      <div slot="title">
        <h4 v-text="formModalTitle"></h4>
      </div>
      <div class="row">
        <div class="col-xl-12">
          <div class="form-horizontal">
            <inputbox
              name="first_name"
              label="First Name"
              :required="true"
              v-model="form.first_name"
              :show-error="form.errors.has('first_name')"
              :error-message="form.errors.get('first_name')"
            ></inputbox>

            <inputbox
              name="last_name"
              label="Last Name"
              :required="true"
              v-model="form.last_name"
              :show-error="form.errors.has('last_name')"
              :error-message="form.errors.get('last_name')"
            ></inputbox>

            <inputbox
              name="email"
              label="Email Address"
              type="email"
              :required="true"
              v-model="form.email"
              :show-error="form.errors.has('email')"
              :error-message="form.errors.get('email')"
            ></inputbox>

            <inputbox
              name="password"
              label="Password"
              type="password"
              :required="true"
              v-model="form.password"
              :show-error="form.errors.has('password')"
              v-if="editingUser === null"
              :error-message="form.errors.get('password')"
            ></inputbox>

            <selectlist
              name="role"
              label="Role"
              v-model="form.role"
              placeholder="Please Select"
              :options="roles"
              @input="onRoleChange"
            ></selectlist>

            <selectlist
              v-if="isSolicitorRole"
              name="solicitor_id"
              label="Solicitor Company"
              v-model="form.solicitor_id"
              placeholder="Please Select"
              :options="solicitors"
              key-name="id"
              label-name="name"
              :show-error="form.errors.has('solicitor_id')"
              :error-message="form.errors.get('solicitor_id')"
            ></selectlist>
          </div>
        </div>
      </div>
      <div slot="footer">
        <button
          class="btn btn-danger float-left"
          v-on:click.prevent="closeModal"
        >
          Close
        </button>
        <form-button :indicator="saving" v-on:clicked="saveForm"
          >Save User
        </form-button>
      </div>
    </modal>
  </div>
</template>

<script>
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

export default {
  components: {
    PageHeader,
    PageHeaderAction
  },

  data() {
    return {
      users: [],
      paginator_data: {},
      modals: {
        form: false
      },
      loading: false,
      saving: false,
      editingUser: null,
      form: new window.Form({
        first_name: null,
        last_name: null,
        email: null,
        password: null,
        role: '',
        solicitor_id: null
      })
    };
  },

  computed: {
    formModalTitle() {
      if (this.editingUser !== null) {
        return 'Edit User';
      }

      return 'Add User';
    },

    showPagination() {
      return (
        this.paginator_data.next_page_url || this.paginator_data.prev_page_url
      );
    },

    tenantIdentifier() {
      if (this.$store.getters.user && this.$store.getters.user.tenant) {
        return this.$store.getters.user.tenant.identifier;
      }

      return '';
    },

    roles() {
      return this.$store.getters['Roles/all'];
    },

    solicitors() {
      return this.$store.getters['Companies/solicitors'];
    },

    isSolicitorRole() {
      if (!this.form.role) return false;
      const selectedRole = this.roles.find(
        r => String(r.id) === String(this.form.role)
      );
      return selectedRole && selectedRole.name === 'solicitor';
    }
  },

  mounted() {
    this.getAllUsers();
    this.$store.commit('Companies/set', []);
    this.$store.commit('Companies/retrieve');
  },

  methods: {
    onRoleChange() {
      if (!this.isSolicitorRole) {
        this.form.solicitor_id = null;
      }
    },

    getAllUsers() {
      this.getData(
        `/credit-hire-organisations/${this.tenantIdentifier}/users?per_page=${this.perPage}`
      );
    },

    getData(url) {
      window.Ajax.get(url)
        .then(response => {
          this.users = response.data.data;
          this.paginator_data = response.data;
        })
        .catch(error => {});
    },

    saveForm() {
      if (!this.isSolicitorRole) {
        this.form.solicitor_id = null;
      }

      let method = 'post';
      let url = `/credit-hire-organisations/${this.tenantIdentifier}/users`;

      if (this.editingUser !== null) {
        method = 'put';
        url = `/credit-hire-organisations/${this.tenantIdentifier}/users/${this.editingUser.identifier}`;
      }

      this.saving = true;

      this.form[method](url)
        .then(response => {
          if (this.editingUser !== null) {
            const index = this.users.indexOf(this.editingUser);

            this.users[index] = response;
            this.saving = false;

            this.closeModal();
          } else {
            this.saving = false;
            this.users.push(response);
            this.closeModal();
          }
        })
        .catch(error => {
          this.saving = false;
          window.toastify.error(
            'Error',
            'Sorry, there was an issue saving the user'
          );
        });
    },

    removeUser(user) {
      window
        .swal({
          title: 'Are you sure?',
          text: "You won't be able to revert this!",
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, delete the user!'
        })
        .then(() => {
          window.Ajax.delete(
            `/credit-hire-organisations/${this.tenantIdentifier}/users/${user.identifier}`
          )
            .then(response => {
              window.toastify.success('Success', 'The user was deleted.');

              const key = this.users.indexOf(user);

              this.users.splice(key, 1);
            })
            .catch(err => {
              window.toastify.error(
                'Error',
                'Sorry, there was an issue deleting the user.'
              );
            });
        });
    },

    openModal() {
      this.modals.form = true;
    },

    closeModal() {
      this.modals.form = false;
      this.editingUser = null;
      this.form.reset();
    },

    openEditModal(user) {
      this.editingUser = user;

      this.form.first_name = user.first_name;
      this.form.last_name = user.last_name;
      this.form.email = user.email;
      this.form.role = user.role_id;
      this.form.solicitor_id = user.solicitor_id || null;
      this.modals.form = true;
    },

    lock(user) {
      window
        .swal({
          title: 'Are you sure?',
          text: "Are you sure want to lock that user's account?",
          type: 'warning',
          showCancelButton: true
        })
        .then(() => {
          window.Ajax.put(
            `/credit-hire-organisations/${this.tenantIdentifier}/users/${user.identifier}/lock`,
            {}
          )
            .then(response => {
              window.toastify.success('Success', 'The account was locked.');

              user.locked_at = response.data.locked_at;
            })
            .catch(err => {
              window.toastify.error(
                'Error',
                'Sorry, there was an issue locking the user.'
              );
            });
        });
    },

    unlock(user) {
      window
        .swal({
          title: 'Are you sure?',
          text: "Are you sure want to unlock that user's account?",
          type: 'warning',
          showCancelButton: true
        })
        .then(() => {
          window.Ajax.put(
            `/credit-hire-organisations/${this.tenantIdentifier}/users/${user.identifier}/unlock`,
            {}
          )
            .then(response => {
              window.toastify.success('Success', 'The account was unlocked.');

              user.locked_at = null;
            })
            .catch(err => {
              window.toastify.error(
                'Error',
                'Sorry, there was an issue unlocking the user.'
              );
            });
        });
    }
  }
};
</script>
